import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getRelativeTimeString } from "@/lib/dates";
import type { UserSource } from "../../types";
import { UserSourceActions } from "./user-source-actions";

export const createUserSourceColumns = ({
  gatewayCountMap = {},
  gatewaysIsPending = false,
  gatewaysIsError = false,
}: {
  gatewayCountMap?: Record<string, number>;
  gatewaysIsPending?: boolean;
  gatewaysIsError?: boolean;
} = {}): ColumnDef<UserSource>[] => [
  {
    id: "name",
    accessorKey: "name",
    meta: { displayName: "Name", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => (
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger>
            <span className="line-clamp-1 max-w-[200px] cursor-help truncate">
              {row.original.name}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="break-all text-sm">{row.original.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    id: "description",
    accessorKey: "description",
    meta: { displayName: "Description", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      const description = row.original.description;
      if (!description) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger>
              <span className="line-clamp-2 max-w-md cursor-help overflow-hidden text-ellipsis">
                {description}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="whitespace-pre-wrap text-sm">{description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    id: "issuer",
    accessorKey: "issuer",
    meta: { displayName: "Issuer", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Issuer" />
    ),
    cell: ({ row }) => (
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger>
            <span className="line-clamp-1 max-w-xs cursor-help truncate font-mono text-sm">
              {row.original.issuer}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="break-all text-sm">{row.original.issuer}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    id: "client_id",
    accessorKey: "client_id",
    meta: { displayName: "Client ID", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Client ID" />
    ),
    cell: ({ row }) => (
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger>
            <span className="line-clamp-1 max-w-xs cursor-help truncate font-mono text-sm">
              {row.original.client_id}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="break-all text-sm">{row.original.client_id}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
  },
  {
    id: "status",
    accessorKey: "status",
    meta: { displayName: "Status", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const isActive = row.original.status === "active";
      return (
        <Badge variant={isActive ? "success" : "secondary"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      );
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    meta: { displayName: "Created", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.created_at);
      const label = Number.isNaN(date.getTime())
        ? row.original.created_at
        : getRelativeTimeString(date);
      return <span>{label}</span>;
    },
  },
  {
    id: "gateways_using",
    accessorFn: (row) => gatewayCountMap[row.id] ?? 0,
    enableSorting: !(gatewaysIsPending || gatewaysIsError),
    meta: { displayName: "Gateways using", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Gateways using" />
    ),
    cell: ({ row }) => {
      if (gatewaysIsPending) {
        return <Skeleton className="h-4 w-6" />;
      }
      if (gatewaysIsError) {
        return <span className="text-muted-foreground">—</span>;
      }
      return <span>{gatewayCountMap[row.original.id] ?? 0}</span>;
    },
  },
  {
    id: "actions",
    meta: { displayName: "Actions", defaultVisible: true },
    header: () => <div className="flex items-center space-x-2">Actions</div>,
    cell: ({ row }) => (
      <UserSourceActions
        gatewayCount={gatewayCountMap[row.original.id] ?? 0}
        gatewaysIsError={gatewaysIsError}
        gatewaysIsPending={gatewaysIsPending}
        userSource={row.original}
      />
    ),
  },
];
